import { plantaApi } from "../../api/fetch";
import { HeadCount, PlanView, PlanViewData } from "../../types/api";
import { View } from "../../types/client";
import { getQueryString } from "../../utils/queryParams";
import { ORG_NODE_BY_ID, orgNodes$ } from "../org/state";
import { period$, periodPlan$, totalPeriodPlan$ } from "../period";
import { headcount$, isLoadingPublishedPlan$ } from "./state";
import { sumDayHeadcountsByWeek } from "./transforms";

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_REFRESH_THRESHOLD = 4 * 60 * 1000; // 4 minutes (refresh before expiry)

interface HeadcountCacheEntry {
  data: PlanViewData;
  timestamp: number;
}

interface ActiveRequestEntry {
  readonly controller: AbortController;
  readonly promise: Promise<PlanViewData>;
}

interface GetHeadcount {
  readonly view: View;
  readonly orgNodeId: string;
  readonly period: Readonly<[string, string]>;
  readonly signal?: AbortSignal;
  readonly level?: string;
}
const headcountRequestCache = new Map<string, HeadcountCacheEntry>();
const activeRequests = new Map<string, ActiveRequestEntry>();

const EMPTY_PLAN_VIEW_DATA: PlanViewData = { data: [] };

const createAbortError = (): Error => {
  try {
    return new DOMException("Aborted", "AbortError");
  } catch {
    const error = new Error("Aborted");
    error.name = "AbortError";
    return error;
  }
};

const abortPromise = (
  signal: AbortSignal,
): Promise<never> & { cleanup: () => void } => {
  let listener: (() => void) | null = null;
  const cleanup = (): void => {
    if (listener !== null) {
      signal.removeEventListener("abort", listener);
      listener = null;
    }
  };

  const promise = new Promise<never>((_, reject) => {
    if (signal.aborted) {
      reject(createAbortError());
      return;
    }

    listener = () => {
      cleanup();
      reject(createAbortError());
    };

    signal.addEventListener("abort", listener, { once: true });
  });

  return Object.assign(promise, { cleanup });
};

const anyAbortSignal = (
  signalA?: AbortSignal,
  signalB?: AbortSignal,
): AbortSignal | undefined => {
  if (signalA === undefined) return signalB;
  if (signalB === undefined) return signalA;

  const controller = new AbortController();
  let listenerA: (() => void) | null = null;
  let listenerB: (() => void) | null = null;

  const cleanup = (): void => {
    if (listenerA !== null) {
      signalA.removeEventListener("abort", listenerA);
      listenerA = null;
    }
    if (listenerB !== null) {
      signalB.removeEventListener("abort", listenerB);
      listenerB = null;
    }
  };

  const abortFrom = (signal: AbortSignal): void => {
    cleanup();
    controller.abort(signal.reason);
  };

  if (signalA.aborted || signalB.aborted) {
    controller.abort(signalA.aborted ? signalA.reason : signalB.reason);
    return controller.signal;
  }

  listenerA = () => {
    abortFrom(signalA);
  };
  listenerB = () => {
    abortFrom(signalB);
  };
  signalA.addEventListener("abort", listenerA, { once: true });
  signalB.addEventListener("abort", listenerB, { once: true });

  return controller.signal;
};

const withAbortSignal = <T>(
  promise: Promise<T>,
  signal?: AbortSignal,
): Promise<T> => {
  if (signal === undefined) {
    return promise;
  }

  const abortPromiseWithCleanup = abortPromise(signal);
  const racePromise = Promise.race([promise, abortPromiseWithCleanup]);

  void promise
    .finally(() => {
      abortPromiseWithCleanup.cleanup();
    })
    .catch(() => undefined);

  return racePromise;
};

const generateCacheKey = ({
  view,
  orgNodeId,
  period,
  level,
}: GetHeadcount): string => {
  const apiView =
    view === View.Day
      ? PlanView.DayQuarters
      : view === View.Month
        ? "month"
        : PlanView.Day;

  const params = getQueryString({ level });
  return `${apiView}/${orgNodeId}?period=${period.toString()}&${params}`;
};

const makeApiRequest = async (args: GetHeadcount): Promise<PlanViewData> => {
  const { view, orgNodeId, period, signal, level } = args;
  const apiView =
    view === View.Day
      ? PlanView.DayQuarters
      : view === View.FcWeek || view === View.Period
        ? PlanView.Day
        : PlanView.Day;

  const params = getQueryString({ level });
  const url = `/headcount/plan/${apiView}/${orgNodeId}?period=${period.toString()}&${params}`;

  return plantaApi.jsonInJsonOut<PlanViewData>(url, {
    method: "GET",
    signal,
  });
};

const refreshCacheInBackground = async (
  cacheKey: string,
  args: GetHeadcount,
): Promise<void> => {
  try {
    const result = await makeApiRequest(args);
    headcountRequestCache.set(cacheKey, {
      data: result,
      timestamp: Date.now(),
    });
  } catch (error) {
    headcountRequestCache.delete(cacheKey);
  }
};

export const clearHeadcountCache = (): void => {
  headcountRequestCache.clear();
  activeRequests.forEach(({ controller }) => {
    try {
      controller.abort();
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        console.error("Error aborting request:", error);
      }
    }
  });
  activeRequests.clear();
};

const updateSignals = (
  cache: HeadcountCacheEntry,
  view: View,
  currentPeriod?: Readonly<[string, string]>,
): void => {
  if (view === View.Week) {
    periodPlan$.value = cache.data.data.map((d) => ({
      ...d,
      dayQuarters: d.dayQuarters.map((v) => (v === -1 ? 0 : v)),
    }));
  }

  if (view === View.Period) {
    const period = currentPeriod ?? period$.value;
    const groupedByWeek: HeadCount[] = cache.data.data.map((d) => ({
      ...d,
      dayQuarters: sumDayHeadcountsByWeek(
        d.dayQuarters,
        new Date(period[0]),
        new Date(period[1]),
      ),
    }));

    totalPeriodPlan$.value = groupedByWeek;
  }
};
export const getHeadcountRequest = async ({
  period,
  orgNodeId,
  view,
  signal,
  level,
}: GetHeadcount): Promise<PlanViewData> => {
  if (!orgNodeId?.trim()) {
    return Promise.resolve(EMPTY_PLAN_VIEW_DATA);
  }
  if (orgNodes$.value.length > 0 && !ORG_NODE_BY_ID.has(orgNodeId)) {
    return Promise.resolve(EMPTY_PLAN_VIEW_DATA);
  }

  const cacheKey = generateCacheKey({ view, orgNodeId, period, level });

  const cached = headcountRequestCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    updateSignals(cached, view, period);
    return cached.data;
  }

  if (
    cached &&
    Date.now() - cached.timestamp < CACHE_DURATION + CACHE_REFRESH_THRESHOLD
  ) {
    refreshCacheInBackground(cacheKey, {
      view,
      orgNodeId,
      period,
      signal,
      level,
    });
    updateSignals(cached, view, period);
    return cached.data;
  }

  const activeRequest = activeRequests.get(cacheKey);
  if (activeRequest !== undefined) {
    return withAbortSignal(activeRequest.promise, signal);
  }

  const controller = new AbortController();
  const requestSignal = anyAbortSignal(signal, controller.signal);

  // Abort listener to clean up activeRequests immediately
  const abortListener = (): void => {
    activeRequests.delete(cacheKey);
  };

  // Set activeRequests entry before creating promise to avoid race condition
  const promise = makeApiRequest({
    view,
    orgNodeId,
    period,
    signal: requestSignal,
    level,
  })
    .then((apiRes) => {
      if (view === View.Week) {
        periodPlan$.value = apiRes.data.map((d) => ({
          ...d,
          dayQuarters: d.dayQuarters.map((v) => (v === -1 ? 0 : v)),
        }));
      }

      if (view === View.Period) {
        const groupedByWeek: HeadCount[] = apiRes.data.map((d) => ({
          ...d,
          dayQuarters: sumDayHeadcountsByWeek(
            d.dayQuarters,
            period$.value[0],
            period$.value[1],
          ),
        }));

        totalPeriodPlan$.value = groupedByWeek;
        return { data: groupedByWeek };
      }

      return apiRes;
    })
    .then((result) => {
      headcountRequestCache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });
      return result;
    })
    .catch((error) => {
      headcountRequestCache.delete(cacheKey);
      throw error;
    })
    .finally(() => {
      if (signal) {
        signal.removeEventListener("abort", abortListener);
      }
      activeRequests.delete(cacheKey);
    });

  // Store active request
  activeRequests.set(cacheKey, { controller, promise });

  if (signal) {
    // If the signal is already aborted, this listener will fire immediately and clean up the entry which was just set
    signal.addEventListener("abort", abortListener);
  }

  return withAbortSignal(promise, signal);
};

export const getHeadcount = async (
  args: GetHeadcount,
): Promise<ReadonlyArray<HeadCount> | undefined> => {
  try {
    isLoadingPublishedPlan$.value = true;
    const { data } = await getHeadcountRequest(args);
    headcount$.value = [...data];
    return data;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return undefined;
    }
    throw error;
  } finally {
    isLoadingPublishedPlan$.value = false;
  }
};

interface PutHeadcount {
  readonly orgId: string;
  readonly day: string;
  readonly dayQuarters?: ReadonlyArray<number>;
  readonly planningMethod?: number;
}

export const putHeadcount = async ({
  orgId,
  day,
  dayQuarters,
  planningMethod,
}: PutHeadcount): Promise<void> => {
  const body = {
    dayQuarters: dayQuarters,
    planningMethod: planningMethod,
  };

  if (dayQuarters !== undefined) {
    body.dayQuarters = body.dayQuarters?.map((num) => (num === -1 ? 0 : num));
  }
  if (planningMethod !== undefined) {
    body.planningMethod = planningMethod;
  }

  await plantaApi.jsonInVoidOut(`/headcount/${orgId}/${day}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

  clearHeadcountCache();
};

export const setHeadcountInServer = async ({
  day,
  values,
}: {
  day: string;
  values: { id: string; value: number[] }[];
}): Promise<void> => {
  // Update data in Server
  const promises = Array.from(values).map(({ id, value }) =>
    putHeadcount({ orgId: id, day, dayQuarters: value }),
  );
  await Promise.all(promises);

  clearHeadcountCache();
};
